import React, { useMemo } from 'react';

import { Select } from '@aline/common';
import { routes } from '@aline/routes';
import { matchPath, useLocation } from 'react-router-dom';
import { ProfileFlyoutMenu } from 'src/components';
import { ContactSupport } from 'src/components/HelpMenu';
import { EntrypointButton } from 'src/components/sections/EntrypointButton';
import { Flag } from 'src/helpers/feature-flags';
import { useResponsiveOverflow } from 'src/hooks/dom-hooks';
import { SignatureDialog } from 'src/layout-engine/components';
import { makeStyles } from 'tss-react/mui';

import { GetStartedNavItem } from './GetStartedNavItem';
import { LeftNavItem } from './LeftNavItem';
import { MoreNavButton, MoreNavMenu } from './MoreNavMenu';
import type { AppSection } from '../ui-interfaces';
import { getRouteForAppSection, LeftNavSection, sectionsInOrder } from '../ui-interfaces';

export const LEFT_NAV_BACKGROUND_COLOR = '#1A2551';

const useStyles = makeStyles()(theme => ({
  drawer: {
    alignItems: 'center',
    backgroundColor: LEFT_NAV_BACKGROUND_COLOR,
    boxSizing: 'border-box',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100%',
    paddingTop: theme.spacing(2),
  },
  measuringContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    opacity: 0,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(1.5),
    pointerEvents: 'none',
    position: 'absolute',
    width: '100%',
  },
  scrollableMiddle: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(0.5),
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(1.5),
    position: 'relative',
    width: '100%',
  },
  stickyBottom: {
    borderTop: '1px solid rgba(255, 255, 255, 0.25)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    width: '100%',
  },
}));

export function LeftNavDrawer(): JSX.Element {
  const { classes } = useStyles();
  const selectedNodeId = useSelectedNodeId();
  const { containerRef, getItemRef, overflowRef, visibleCount } =
    useResponsiveOverflow<HTMLDivElement>(sectionsInOrder.length, 'vertical');

  const visibleSections = sectionsInOrder.slice(0, visibleCount);
  const overflowSections = sectionsInOrder.slice(visibleCount);

  return (
    <>
      <div className={classes.drawer} data-cy={Select.leftNav.drawer}>
        <EntrypointButton />

        {/* SCROLLABLE MIDDLE */}
        <div ref={containerRef} className={classes.scrollableMiddle}>
          {/* Hidden measuring container - renders all items to get accurate measurements */}
          <div aria-hidden inert className={classes.measuringContainer}>
            {sectionsInOrder.map((section, index) => (
              <LeftNavItem
                key={section}
                ref={getItemRef(index)}
                section={section}
                selectedNodeId={selectedNodeId}
              />
            ))}
            <MoreNavButton ref={overflowRef} selected={false} />
          </div>

          {/* Visible nav items */}
          {visibleSections.map(section => (
            <LeftNavItem key={section} section={section} selectedNodeId={selectedNodeId} />
          ))}
          {overflowSections.length > 0 && (
            <MoreNavMenu sections={overflowSections} selectedNodeId={selectedNodeId} />
          )}
        </div>

        {/* STICKY BOTTOM */}
        <div className={classes.stickyBottom}>
          {Flag.GET_STARTED_ONBOARDING && <GetStartedNavItem selectedNodeId={selectedNodeId} />}
          <ContactSupport />
          <ProfileFlyoutMenu />
        </div>
      </div>
      <SignatureDialog />
    </>
  );
}

const leftNavSections = Object.values(LeftNavSection);

function getRoutesToTest(leftNavSection: AppSection): string[] {
  switch (leftNavSection) {
    case LeftNavSection.AI_CHAT:
      return [routes.ChatIndex, routes.ChatDetail];
    case LeftNavSection.DASHBOARD:
      return [routes.DashboardIndex];
    case LeftNavSection.COMMUNITY:
      return [routes.CommunityIndex];
    case LeftNavSection.GET_STARTED:
      return [routes.GetStarted];
    case LeftNavSection.WORKFLOWS:
      return [routes.WorkflowIndexMustRedirect, routes.WorkflowDetail];
    case LeftNavSection.TEMPLATES:
      return [
        getRouteForAppSection(leftNavSection),
        routes.TemplatePublished,
        routes.TemplateDraft,
        routes.TemplateSelectedTemplateIndex,
      ];
    case LeftNavSection.ADMIN:
      return [
        getRouteForAppSection(leftNavSection),
        routes.AdminUserIndex,
        routes.AdminGroupIndex,
        routes.AdminRoles,
        routes.AdminContractTypes,
        routes.AdminIntegrations,
        routes.AdminBilling,
      ];
    default:
      return [getRouteForAppSection(leftNavSection)];
  }
}

function useSelectedNodeId(): string {
  const location = useLocation();

  return useMemo(() => {
    const matchedSection = leftNavSections.find(path => {
      const routesToTest = getRoutesToTest(path);

      return routesToTest.some(path => {
        return Boolean(matchPath({ end: false, path }, location.pathname));
      });
    });

    return matchedSection ? getRouteForAppSection(matchedSection) : '';
  }, [location.pathname]);
}
